import React from "react";
import { useTranslation } from "react-i18next";
import classNames from "classnames";
import { FaCheck } from "react-icons/fa";
import { CreateCommunityForm } from "../../../../types/forms";
import useAuth from "../../../../hooks/account/useAuth";
import { CommunityType } from "../../../../types/community";
import { getMinimumSubscriptionForFeatures } from "../../../../utils/FeatureUtils";
import { SubscriptionType } from "../../../../types/misc";
import { StepFlowStep } from "../../../components/misc/StepFlow";
import IconBadge from "../../../components/icons/IconBadge";
import { communityTypeIcons } from "../../../../utils/iconUtils";

type CommunitySetupViewProps = {
  setStep: (step: 1 | -1) => void;
  community: CreateCommunityForm;
  setCommunity: (community: CreateCommunityForm) => void;
}

export default function CommunitySetupView({ community, setCommunity, setStep }: CommunitySetupViewProps) {
  const { t } = useTranslation();
  const auth = useAuth();

  const typeOptions = (
    Object.keys(CommunityType)
  ) as CommunityType[];

  const getDefaultFeatures = (communityType: CommunityType) => {
    switch (communityType) {
      case CommunityType.ROOMMATES:
        return {
          EASY_SHARING: true,
          EVENTS: true,
          PARTICIPATION_INSIGHTS: true,
          TASKS: true,
          STATS_AND_SCORES: true,
          CHATS: true,
          COST_SPLITTING: true,
        };
      case CommunityType.STUDENTGROUP:
        return {
          EASY_SHARING: true,
          MEMBER_ADMINISTRATION: true,
          EVENTS: true,
          PARTICIPATION_INSIGHTS: true,
          STATS_AND_SCORES: true,
          COMMUNICATION: true,
          CHATS: true,
          COST_SPLITTING: true,
          PHOTO_ALBUMS: true,
        };
      case CommunityType.SPORTTEAM:
        return {
          EASY_SHARING: true,
          MEMBER_ADMINISTRATION: true,
          EVENTS: true,
          PARTICIPATION_INSIGHTS: true,
          TASKS: true,
          STATS_AND_SCORES: true,
          COMMUNICATION: true,
          CHATS: true,
          COST_SPLITTING: true,
        };
      case CommunityType.ASSOCIATION:
        return {
          EASY_SHARING: true,
          MEMBER_ADMINISTRATION: true,
          EVENTS: true,
          PARTICIPATION_INSIGHTS: true,
          TASKS: true,
          STATS_AND_SCORES: true,
          COMMUNICATION: true,
          CHATS: true,
          COST_SPLITTING: true,
          FINANCIAL_ADMINISTRATION: true,
          PHOTO_ALBUMS: true,
        };
      case CommunityType.COLLEAGUES:
        return {
          EASY_SHARING: true,
          EVENTS: true,
          TASKS: true,
          CHATS: true,
          COST_SPLITTING: true,
        };
      case CommunityType.FAMILY:
        return {
          EASY_SHARING: true,
          EVENTS: true,
          CHATS: true,
          COST_SPLITTING: true,
          PHOTO_ALBUMS: true,
        };
      case CommunityType.FRIENDS:
        return {
          EASY_SHARING: true,
          EVENTS: true,
          CHATS: true,
          COST_SPLITTING: true,
          PHOTO_ALBUMS: true,
        };
      default:
        return {};
    }
  };

  const handleTypeSelection = (type: CommunityType) => {
    const isDeselecting = type === community.type;
    const newcommunityType = isDeselecting ? undefined : type;
    const newFeatures = newcommunityType ? getDefaultFeatures(newcommunityType) : undefined;
    const requiredSubscription = newFeatures
      ? getMinimumSubscriptionForFeatures(newFeatures)
      : SubscriptionType.FREE;

    setCommunity({
      ...community,
      type: newcommunityType,
      features: newFeatures,
      connectedMembers: newFeatures ? newFeatures.EASY_SHARING : false,
      subscriptionType: requiredSubscription,
    });
  };

  return (
    <StepFlowStep
      setStep={setStep}
      disabled={!community.type || !community.name}
      showPrev={!auth}
      title={t('page.onboarding.community.type.title')}
      subtitle={t(`page.onboarding.community.type.subtitle`)}
      className="max-w-4xl">
      <div className="flex flex-col gap-6 pr-2">
        <div className="relative flex w-1/2 flex-shrink-0 flex-col gap-1">
          <div className="hidden items-start justify-between gap-1 sm:flex">
            <p className="font-semibold">{t(`page.onboarding.community.info.name`)}</p>
          </div>
          <div className="relative w-full">
            <div className="h-10 sm:hidden"> </div>
            <input
              type="text"
              className="w-full"
              placeholder={`${t('general.name')}...`}
              value={community.name || ''}
              onChange={(e) => setCommunity({ ...community, name: e.target.value })}
            />
            <p className="absolute right-4 top-2 text-sm sm:hidden">{t('general.mandatory')}</p>
          </div>
        </div>

        <div className="flex flex-col gap-1">
          <p className="font-semibold">{t('page.onboarding.community.info.type')}</p>
          <div
            className="grid gap-3 lg:gap-4"
            style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))' }}>
            {typeOptions.map((type) => (
              <div
                key={type}
                onClick={() => handleTypeSelection(type)}
                className={classNames(
                  'group relative cursor-pointer rounded-xl border-2 p-4 transition-all duration-300 hover:shadow-md',
                  {
                    'border-primary-900 bg-opacity-10 shadow-sm': community.type === type,
                    'border-secondary-200 bg-secondary-200 bg-opacity-30 hover:border-primary-900 hover:bg-opacity-40':
                      community.type !== type,
                  },
                )}>
                <div
                  className={classNames(
                    'absolute right-2 top-2 flex h-5 w-5 items-center justify-center rounded-full border-2 transition-all duration-200',
                    {
                      'border-primary-900 bg-primary-900': community.type === type,
                      'border-secondary-200 group-hover:border-primary-900':
                        community.type !== type,
                    },
                  )}>
                  {community.type === type && (
                    <FaCheck className="h-2.5 w-2.5 text-secondary-50" />
                  )}
                </div>

                <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-lg transition-all duration-200">
                  <IconBadge
                    className={classNames({
                      'bg-primary-900 text-secondary-200': community.type === type,
                      'bg-secondary-200 text-primary-900': community.type !== type,
                    })}
                    icon={communityTypeIcons[type]}
                  />
                </div>

                <div className="space-y-1">
                  <h3 className="text-base font-semibold text-primary-900">
                    {t(`page.onboarding.community.type.${type.toLowerCase().split('_')[0]}.name`)}
                  </h3>
                  <p className="text-xs leading-relaxed text-primary-900 opacity-75">
                    {t(`page.onboarding.community.type.${type.toLowerCase().split('_')[0]}.description`)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </StepFlowStep>
  );
}